import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { INVENTARIO, RESERVA_BASE } from './reserva';
import { vitrine, formatarPreco, detalhesDaCasa, STATUS } from './casasApi';

/*
  Páginas Terrenos e Casas da rota reserva (tarefa 6f99acda). Modelo de lista de
  imóveis nas referências que a Paola mandou em 22/09 (morebempira e bendiz):
  cartão com foto, local, preço de chamada e atalho para falar com o corretor.
*/

const azul = '#005BAA';
const tinta = '#13314e';

// Botões que substituem a marca Momentum no topo. `claro` é para fundo de foto
// (hero), `escuro` para fundo branco (estas páginas).
const NavWrap = styled.nav`
  display: flex; gap: 10px; justify-content: center;
  a {
    pointer-events: auto;
    font-size: 12px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase;
    padding: 9px 18px; border-radius: 999px; text-decoration: none;
    border: 1px solid ${(p) => (p.$tom === 'claro' ? 'rgba(255,255,255,0.75)' : 'rgba(0,91,170,0.35)')};
    color: ${(p) => (p.$tom === 'claro' ? '#fff' : azul)};
    background: ${(p) => (p.$tom === 'claro' ? 'rgba(6,36,61,0.28)' : '#fff')};
    backdrop-filter: blur(6px);
    text-shadow: ${(p) => (p.$tom === 'claro' ? '0 1px 4px rgba(0,0,0,0.6)' : 'none')};
  }
  a[aria-current='page'] { background: ${azul}; color: #fff; border-color: ${azul}; text-shadow: none; }
`;

export function ReservaNav({ ativo, tom = 'claro' }) {
  return (
    <NavWrap $tom={tom} aria-label="Imóveis">
      <Link to={`${RESERVA_BASE}/terrenos`} aria-current={ativo === 'terrenos' ? 'page' : undefined}>Terrenos</Link>
      <Link to={`${RESERVA_BASE}/casas`} aria-current={ativo === 'casas' ? 'page' : undefined}>Casas</Link>
    </NavWrap>
  );
}

// Rota reserva fica fora de busca: pedido explícito de "ninguém acessar".
// Recebe `ativo` porque a RivieraLP chama sempre (ordem de hooks) e só a
// variante reserva liga.
export function useNoIndex(ativo = true) {
  useEffect(() => {
    if (!ativo) return undefined;
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => m.remove();
  }, [ativo]);
}

const Page = styled.main`
  min-height: 100svh; background: #f7fbfd; color: ${tinta};
  font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
`;
const Top = styled.header`
  background: #fff; border-bottom: 1px solid #e3edf5; padding: 16px;
  display: grid; gap: 14px; justify-items: center;
  /* logo.png tem muita margem branca: 38px deixava o nome ilegível no celular */
  img { height: 84px; width: auto; margin: -14px 0; }
`;
const Body = styled.section`
  max-width: 1080px; margin: 0 auto; padding: 28px 16px 64px;
  h1 { font-size: 26px; margin: 0 0 6px; }
  .sub { color: #56708a; margin: 0 0 22px; }
`;
const Grid = styled.div`
  display: grid; gap: 18px; grid-template-columns: 1fr;
  @media (min-width: 680px) { grid-template-columns: 1fr 1fr; }
  @media (min-width: 1000px) { grid-template-columns: 1fr 1fr 1fr; }
`;
const Card = styled(Link)`
  display: block; background: #fff; border-radius: 16px; overflow: hidden; text-decoration: none; color: inherit;
  box-shadow: 0 6px 22px rgba(19,49,78,0.10); border: 1px solid #e3edf5;
  img { width: 100%; aspect-ratio: 4 / 3; object-fit: cover; display: block; }
  .txt { padding: 16px; }
  .nome { font-weight: 800; font-size: 17px; }
  .local { color: #56708a; font-size: 13px; margin-top: 4px; }
  .preco { margin-top: 12px; font-weight: 800; color: ${azul}; }
  ul { margin: 10px 0 0; padding: 0; list-style: none; display: flex; flex-wrap: wrap; gap: 6px; }
  li { font-size: 12px; background: #eef5fb; color: ${tinta}; padding: 4px 9px; border-radius: 999px; }
`;
const Vazio = styled.div`
  background: #fff; border: 1px dashed #b8cde0; border-radius: 16px; padding: 28px 20px; text-align: center;
  a { display: inline-block; margin-top: 14px; background: ${azul}; color: #fff; padding: 12px 20px; border-radius: 999px; text-decoration: none; font-weight: 700; }
`;

const TITULO = { terrenos: 'Terrenos', casas: 'Casas' };
const SUB = {
  terrenos: 'Lotes em loteamentos fechados à beira da Represa Jurumirim.',
  casas: 'Casas em condomínio, prontas para morar ou para o fim de semana.',
};

// Casa do cadastro (vitrine_publica) no formato do cartão. Sem foto, usa a vista
// aérea do condomínio para o cartão não ficar com buraco.
function casaParaCartao(c) {
  const capa = (c.fotos || [])[0];
  const detalhes = detalhesDaCasa(c);
  if (c.status === 'reservado') detalhes.unshift(STATUS.reservado);
  return {
    nome: c.titulo,
    local: 'Riviera de Santa Cristina XIII · Paranapanema, SP',
    destaque: formatarPreco(c.preco) || 'Consulte o valor',
    detalhes,
    foto: capa ? capa.url : '/riviera/hero-peninsula.jpg',
    href: `${RESERVA_BASE}/casas/${c.id}`,
    chave: c.id,
  };
}

export default function RivieraInventario({ tipo }) {
  useNoIndex();
  useEffect(() => { document.title = `${TITULO[tipo]} · Riviera de Santa Cristina`; }, [tipo]);
  // Casas vêm do cadastro que o Fábio faz no site (tarefa 87c272e9). Se a leitura
  // falhar, fica a lista fixa de antes, que mostra o convite para receber opções.
  const [doCadastro, setDoCadastro] = useState(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    if (tipo !== 'casas') return undefined;
    let vivo = true;
    vitrine().then((lista) => { if (vivo) setDoCadastro(lista.map(casaParaCartao)); })
      .catch(() => { if (vivo) setFalhou(true); });
    return () => { vivo = false; };
  }, [tipo]);
  // Enquanto a vitrine não responde, não mostra o "estamos montando" para logo trocar por casas.
  const carregando = tipo === 'casas' && doCadastro === null && !falhou;
  const itens = (tipo === 'casas' && doCadastro) ? doCadastro : (INVENTARIO[tipo] || []);
  return (
    <Page>
      <Top>
        <Link to={RESERVA_BASE} aria-label="Voltar ao início"><img src="/riviera/logo.png" alt="Riviera de Santa Cristina XIII" /></Link>
        <ReservaNav ativo={tipo} tom="escuro" />
      </Top>
      <Body>
        <h1>{TITULO[tipo]}</h1>
        <p className="sub">{SUB[tipo]}</p>
        {carregando ? null : itens.length ? (
          <Grid>
            {itens.map((it) => (
              <Card key={it.chave || it.nome} to={it.href}>
                <img src={it.foto} alt={it.nome} loading="lazy" />
                <div className="txt">
                  <div className="nome">{it.nome}</div>
                  <div className="local">{it.local}</div>
                  <div className="preco">{it.destaque}</div>
                  <ul>{it.detalhes.map((d) => <li key={d}>{d}</li>)}</ul>
                </div>
              </Card>
            ))}
          </Grid>
        ) : (
          <Vazio>
            Estamos montando esta seleção. Fale com a gente e receba as opções disponíveis.
            <br />
            <Link to={`${RESERVA_BASE}#agendar`}>Quero receber opções</Link>
          </Vazio>
        )}
      </Body>
    </Page>
  );
}
